import React, { useEffect, useRef } from 'react';
import 'leaflet/dist/leaflet.css';

const B = '#2563eb';

// "Rejoue" la journée : carte avec les arrêts du jour reliés dans l'ordre
// chronologique + timeline en dessous. Utilise les coordonnées déjà
// capturées pour la tournée optimisée (AddressAutocomplete/BAN) — AUCUN
// tracking GPS continu, juste les adresses d'intervention déjà saisies.
export default function JourneyMap({ interventions, darkMode, onClose }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  const stops = interventions
    .filter(i => i.lat != null && i.lng != null)
    .sort((a, b) => (a.heure || '').localeCompare(b.heure || ''));

  useEffect(() => {
    if (!mapRef.current || stops.length === 0) return;
    let map;
    let cancelled = false;
    (async () => {
      const L = (await import('leaflet')).default;
      if (cancelled || !mapRef.current) return;
      map = L.map(mapRef.current);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap',
        maxZoom: 19,
      }).addTo(map);

      const latlngs = stops.map(s => [s.lat, s.lng]);
      L.polyline(latlngs, { color: B, weight: 3, dashArray: '6 6', opacity: 0.8 }).addTo(map);

      stops.forEach((s, i) => {
        const done = s.statut === 'termine';
        const icon = L.divIcon({
          className: '',
          html: `<div style="background:${done ? '#10b981' : B};color:#fff;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:13px;box-shadow:0 2px 8px rgba(0,0,0,0.3);border:2px solid #fff;">${i + 1}</div>`,
          iconSize: [28, 28],
          iconAnchor: [14, 14],
        });
        L.marker([s.lat, s.lng], { icon }).addTo(map)
          .bindPopup(`<b>${s.heure || ''}</b><br>${(s.client || '').replace(/</g, '&lt;')}`);
      });

      map.fitBounds(L.latLngBounds(latlngs), { padding: [40, 40] });
      mapInstanceRef.current = map;
    })();
    return () => {
      cancelled = true;
      if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
    };
  }, [stops.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,10,26,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, padding: '20px' }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: '100%', maxWidth: '900px', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        background: darkMode ? '#1c1c1e' : '#fff', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 24px 70px rgba(0,0,0,0.35)',
      }}>
        <div style={{ padding: '18px 22px', borderBottom: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <p style={{ fontSize: '16px', fontWeight: 800, margin: 0 }}>🗺️ La carte de ma journée</p>
            <p style={{ fontSize: '12px', color: '#9ca3af', margin: '2px 0 0' }}>{stops.length} arrêt{stops.length > 1 ? 's' : ''} géolocalisé{stops.length > 1 ? 's' : ''} aujourd'hui</p>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: '22px', cursor: 'pointer', color: '#9ca3af' }}>×</button>
        </div>

        {stops.length === 0 ? (
          <div style={{ padding: '60px 24px', textAlign: 'center' }}>
            <p style={{ fontSize: '36px', margin: '0 0 10px' }}>📍</p>
            <p style={{ fontSize: '14px', color: '#9ca3af' }}>Aucune intervention géolocalisée aujourd'hui.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flex: 1, overflow: 'hidden', flexDirection: 'row' }}>
            <div ref={mapRef} style={{ flex: '2 1 400px', minHeight: '360px', background: '#eef2ff' }} />
            <div style={{ flex: '1 1 220px', overflowY: 'auto', padding: '16px', borderLeft: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
              {stops.map((s, i) => (
                <div key={s.id} style={{ display: 'flex', gap: '10px', marginBottom: i < stops.length - 1 ? '14px' : 0 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: s.statut === 'termine' ? '#10b981' : B, color: '#fff', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
                    {i < stops.length - 1 && <div style={{ width: '2px', flex: 1, background: darkMode ? '#2c2c2e' : '#e5e7eb', marginTop: '2px' }} />}
                  </div>
                  <div style={{ paddingBottom: '4px' }}>
                    <p style={{ fontSize: '13px', fontWeight: 700, margin: 0 }}>{s.heure || '—'}</p>
                    <p style={{ fontSize: '13px', color: darkMode ? '#d1d5db' : '#374151', margin: '1px 0 0' }}>{s.client}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
